<template>
  <div>
    <v-toolbar
      flat
      color="white"
    >
      <v-toolbar-title data-cy="media-tab">Media</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-dialog
        v-model="dialog"
        max-width="500px"
      >
        <v-btn
          v-show="editMode"
          slot="activator"
          color="primary"
          dark
          class="mb-2 ml-5"
        >
          New Item
        </v-btn>
        <v-card>
          <v-card-title>
            <span class="headline">{{ formTitle }}</span>
          </v-card-title>
          <v-card-text>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12>
                  <v-select
                    :items="mediaTypes"
                    item-text="media_type_desc"
                    item-value="media_type_desc"
                    v-model="editedItem.media_type_desc"
                    :error-messages="editedMediaTypeErrors"
                    label="Media Type"
                    @input="$v.editedItem.media_type_desc.$touch()"
                    @blur="$v.editedItem.media_type_desc.$touch()">
                  </v-select>
                  <v-text-field 
                    v-model="editedItem.media_url" 
                    label="File"
                    class="mb-1"
                    :error-messages="editedMediaUrlErrors"
                    clearable
                    @input="$v.editedItem.media_url.$touch()"
                    @blue="$v.editedItem.media_url.$touch()"
                  >
                  </v-text-field>
                  <upload-btn
                    :fileChangedCallback="onFileSelect"
                    title="Browse"
                    class="white--text mt-2"></upload-btn>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn 
              color="blue darken-1" 
              flat 
              @click.native="close"
            >
              Cancel
            </v-btn>
            <v-btn 
              color="blue darken-1" 
              flat 
              :disabled="(this.editedMediaUrlErrors.length > 0 || editedMediaTypeErrors.length > 0)"
              @click.native="save"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <v-data-table
      :headers="currentHeaders"
      :items="dataset.dataset_media"
      hide-actions
      class="elevation-1"
      style="border-top-style: solid; border-width: 1px; border-color: #D3D3D3;"
    >
      <template 
        slot="items" 
        slot-scope="props"
        :class="{ presentation: getPresentMode }"
      >
        <td
          :class="{ presentation: getPresentMode }">
          {{ props.item.create_date }}
        </td>
        <td
          :class="{ presentation: getPresentMode }">
          {{ props.item.media_type_desc }}
        </td>
        <td 
          class="text-xs" 
          v-if="props.item.media_url"
          :class="{ presentation: getPresentMode }"
        >
          <a
            v-if="props.item.media_url"
            @click="downloadResource('/api/dataset/' + dataset.dataset_key + '/media/' + props.item.media_url, props.item.media_name)"
            target="_blank"
            v-text="props.item.media_name"
          >
          </a>
        </td>
        <td 
          class="text-xs"
          :class="{ presentation: getPresentMode }"
          v-else
        >
          {{ props.item.media_name }}
        </td>
        <td 
          v-if="editMode" 
          class="justify-center mx-auto"
          :class="{ presentation: getPresentMode }"
        >
        <font-awesome-icon
            size="lg"
            class="mx-1"
            :icon="['far', 'trash']"
            @click="deleteItem(props.item)"
          >
          </font-awesome-icon>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import axios from 'axios'
import UploadButton from 'vuetify-upload-button'
import { mapMutations, mapGetters, mapActions, mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'

export default {
  components: {
    'upload-btn': UploadButton
  },
  props: {
    editMode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: false,
      headers: [
        {
          text: 'Date',
          align: 'left',
          sortable: false,
          value: 'create_date'
        },
        { text: 'Media Type', value: 'media_type_desc' },
        { text: 'Media Link', value: 'media_url' },
        { text: 'Actions', value: 'name', sortable: false }
      ],
      editedItem: {
        media_type_desc: '',
        media_url: ''
      },
      defaultItem: {
        media_type_desc: '',
        media_url: ''
      },
      editedKey: 0,
      showDatePicker: false,
      showDropdown: false,
      allowedFileTypes: ['txt', 'pdf', 'png', 'jpg', 'jpeg', 'gif', 'svg'],
      file: null
    }
  },
  validations: {
    editedItem: {
      media_type_desc: {
        required
      },
      media_url: {
        required
      }
    }
  },
  computed: {
    ...mapGetters([
      'getCurrentDataset',
      'getPresentMode',
      'isUserAdmin',
    ]),
    ...mapState({
      mediaTypes: state => state.dataset.transactions.allMediaTypes
    }),
    formTitle () {
      return 'New Item'
    },
    currentHeaders () {
      return this.editMode ? this.headers : this.headers.slice(0, -1)
    },
    dataset () {
      return this.getCurrentDataset
    },
    editedMediaTypeErrors () {
      const errors = []
      if (!this.$v.editedItem.media_type_desc.$dirty) return errors
      !this.$v.editedItem.media_type_desc.required && errors.push('Media Type Required.')
      return errors
    },
    editedMediaUrlErrors () {
      const extension = this.editedItem.media_url ? this.editedItem.media_url.split('.').pop() : ''
      if (!this.$v.editedItem.media_url.$dirty || extension === '') return ['Filename Required']
      const validExtensionString = this.allowedFileTypes.reduce((val, acc) => `${acc} .${val}`, '')
      return this.allowedFileTypes.includes(extension.toLowerCase()) ? []
        : [`Extension .${extension} not allowed. Allowed file types types are ${validExtensionString}`]
    }
  },
  watch: {
    dialog (val) {
      val || this.close()
    }
  },
  methods: {
    ...mapActions([
      'removeMediaAction',
      'addMediaAction',
      'fetchAllMediaTypesAction'
    ]),
    ...mapMutations([
      'logError'
    ]),
    deleteItem (item) {
      const payload = {
        array: 'dataset_media',
        key: item.media_url
      }
      confirm('Are you sure you want to delete this item?') &&
      this.removeMediaAction(payload)
    },
    close () {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
      }, 300)
    },
    save () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        let formData = new FormData()
        formData.append('user_file', this.file)
        formData.append('media_type_desc', this.editedItem.media_type_desc)
        const payload = {
          array: 'dataset_media',
          data: formData
        }
        this.addMediaAction(payload)
      }
      this.close()
    },
    onFileSelect (file) {
      this.$v.editedItem.media_url.$touch()
      if (file != null) {
        this.file = file
        this.editedItem.media_url = file.name
      }
    },
    downloadResource (url, filename) {
      const config = {
        responseType: 'arraybuffer'
      }
      axios.get(url, config)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
        })
    }
  },
  mounted () {
    this.fetchAllMediaTypesAction()
  }
}
</script>
